import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";

const SectionBlock = ({ s }) => {
  const alignCls = s.align === "center" ? "text-center mx-auto" : "text-left";

  switch (s.type) {
    case "hero":
      return (
        <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden flex items-end" data-testid="section-hero">
          {s.image_url && <img src={s.image_url} alt={s.heading || ""} className="absolute inset-0 w-full h-full object-cover grayscale-img" />}
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-ink/10" />
          {s.heading && (
            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full">
              <h1 className={`font-serif text-paper font-black tracking-tight leading-[0.98] text-5xl md:text-7xl ${s.align === "center" ? "text-center" : ""}`}>
                {s.heading}
              </h1>
            </div>
          )}
        </section>
      );
    case "heading":
      return (
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24" data-testid="section-heading">
          <h2 className={`font-serif text-4xl md:text-5xl font-bold text-ink tracking-tight ${alignCls}`}>{s.heading}</h2>
        </div>
      );
    case "text":
      return (
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-6" data-testid="section-text">
          <p className={`font-sans text-lg text-ink/75 leading-relaxed whitespace-pre-line ${alignCls}`}>{s.text}</p>
        </div>
      );
    case "image":
      return s.image_url ? (
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-12" data-testid="section-image">
          <img src={s.image_url} alt={s.heading || ""} className="w-full object-cover grayscale-img" />
        </div>
      ) : null;
    case "button":
      return s.button_url ? (
        <div className={`max-w-3xl mx-auto px-6 md:px-12 pt-10 ${s.align === "center" ? "text-center" : ""}`} data-testid="section-button">
          {s.button_url.startsWith("/") ? (
            <Link to={s.button_url} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors">
              {s.button_label || "Learn more"} <ArrowRight size={18} />
            </Link>
          ) : (
            <a href={s.button_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors">
              {s.button_label || "Learn more"} <ArrowRight size={18} />
            </a>
          )}
        </div>
      ) : null;
    default:
      return null;
  }
};

export default function CustomPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const hasHero = page?.sections?.[0]?.type === "hero";

  return (
    <div className="bg-paper min-h-screen flex flex-col" data-testid="custom-page">
      <Navbar />
      <main className={`flex-1 ${hasHero ? "" : "pt-28 md:pt-36"}`}>
        {notFound && (
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-serif text-4xl font-bold text-ink mb-4">Page not found.</h1>
            <Link to="/" className="font-sans text-trailride font-medium hover:underline">← Back home</Link>
          </div>
        )}
        {page?.sections?.map((s) => <SectionBlock key={s.id} s={s} />)}
        {page && <div className="h-20 md:h-28" />}
      </main>
      <Footer />
    </div>
  );
}
